import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Loader from "react-loader-spinner";
import { FiImage, FiDownload } from "react-icons/fi";
import { SOCIAL_PATTERN_TYPES } from '../constants/config';
import { fetchSocialPatterns } from '../actions/socialPicturesActions';
import {
    normalizePattern,
    isPatternAvailable,
    resolveImage,
} from '../utilities/infographics';
import '../styles/infographics.css';

// Каталог шаблонов инфографики. Перенос вкладки "Публикации в соц сети"
// из приложения (ingame_admin/src/layouts/ManageSocialPosts.js).
// В приложении тип выбирается выпадающим списком — на широком экране
// быстрее чипами, все семь типов видно сразу.

const PAGE_SIZE = 24;

export default function InfographicsCatalog() {
    const dispatch = useDispatch();
    const history = useHistory();
    const userState = useSelector(state => state.user);
    const clientId = userState && userState.userData && userState.userData.data
        ? userState.userData.data.client_id
        : null;

    const [type, setType] = useState(0);
    const [patterns, setPatterns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState(null);

    const loadPage = useCallback(async (nextType, offset) => {
        const isFirstPage = offset === 0;
        if (isFirstPage) {
            setLoading(true);
            setError(null);
        } else {
            setLoadingMore(true);
        }
        try {
            const data = await dispatch(fetchSocialPatterns(nextType, offset, PAGE_SIZE));
            const rows = (Array.isArray(data) ? data : []).map(normalizePattern);
            setPatterns(prev => (isFirstPage ? rows : prev.concat(rows)));
            setHasMore(rows.length === PAGE_SIZE);
        } catch (e) {
            console.error('Ошибка загрузки шаблонов инфографики:', e);
            if (isFirstPage) {
                setPatterns([]);
                setError(e);
            }
            setHasMore(false);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [dispatch]);

    useEffect(() => { loadPage(type, 0); }, [loadPage, type]);

    // Шаблоны других клиентов админу не показываем — как в приложении
    const visible = useMemo(
        () => patterns.filter(pattern => isPatternAvailable(pattern, clientId)),
        [patterns, clientId]
    );

    const openPattern = (pattern) => {
        history.push(`/infographics/${pattern.id}`, { pattern });
    };

    const renderCard = (pattern) => {
        const image = resolveImage(pattern.image);
        return (
            <button
                type="button"
                key={pattern.id}
                className="igCard"
                onClick={() => openPattern(pattern)}
            >
                <div className="igCardPreview">
                    {image ? (
                        <img
                            src={image}
                            alt={pattern.title}
                            loading="lazy"
                            style={{ objectFit: pattern.resizeMode === 'contain' ? 'contain' : 'cover' }}
                        />
                    ) : (
                        <span className="igCardPlaceholder"><FiImage /></span>
                    )}
                </div>
                <span className="igCardTitle">{pattern.title}</span>
            </button>
        );
    };

    return (
        <div className="layoutWrapper">
            <div className="matchCenterWrapper">
                <div className="matchCenterHeader">
                    <span className="matchCenterName">Инфографика</span>
                </div>

                <div className="igPage">
                    <div className="igSectionHead">
                        <div>
                            <h2>Публикации для соцсетей</h2>
                            <p>
                                Выберите шаблон — турнирную таблицу, топ-5, афишу матча
                                или сборную тура. Дальше настроите параметры и скачаете картинку.
                            </p>
                        </div>
                    </div>

                    <div className="igBatchLaunch">
                        <button
                            type="button"
                            className="igGhostBtn"
                            onClick={() => history.push('/infographics/batch?mode=tour')}
                        >
                            <FiDownload /> Скачать все картинки тура
                        </button>
                        <button
                            type="button"
                            className="igGhostBtn"
                            onClick={() => history.push('/infographics/batch?mode=day')}
                        >
                            <FiDownload /> Скачать все картинки дня
                        </button>
                        <button
                            type="button"
                            className="igGhostBtn"
                            onClick={() => history.push('/infographics/batch?mode=leagues')}
                        >
                            <FiDownload /> Таблицы и топ-5 по всем лигам
                        </button>
                    </div>

                    <div className="igTypeFilter">
                        {SOCIAL_PATTERN_TYPES.map(item => (
                            <button
                                type="button"
                                key={item.value}
                                className={`igChip ${type === item.value ? 'igChipActive' : ''}`}
                                onClick={() => setType(item.value)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="igLoader">
                            <Loader type="Oval" color="#04a9f5" height={64} width={64} />
                            <p>Загрузка шаблонов…</p>
                        </div>
                    ) : error ? (
                        <div className="igError">
                            Ошибка загрузки:{' '}
                            {(error.response && error.response.data) || error.message || 'неизвестная ошибка'}
                        </div>
                    ) : visible.length === 0 ? (
                        <div className="igEmptyState">
                            <p>Для этого типа шаблонов пока нет.</p>
                        </div>
                    ) : (
                        <>
                            <div className="igGrid">{visible.map(renderCard)}</div>
                            {hasMore && (
                                <div className="igMoreRow">
                                    <button
                                        type="button"
                                        className="igGhostBtn"
                                        disabled={loadingMore}
                                        onClick={() => loadPage(type, patterns.length)}
                                    >
                                        {loadingMore ? 'Загружаем…' : 'Показать ещё'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
